import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Globe, Layers, Plus, Save, Search, Trash2, X } from 'lucide-react';
import { useApp } from '../../context/AppContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Admin UI to CRUD destination groups: an admin creates a group (e.g. Indonesia
// Standard / Lite / Premium), each member country has a short descriptor shown
// to users inside the picker modal on the public site.
const DestinationGroupingPanel = () => {
  const { allCountries, refreshDestinationGroups } = useApp();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { id?, name, primary, members:[{country_id,label,descriptor}] }
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // The admin bearer token is already attached globally to axios.defaults by
  // AppContext when the admin signs in — passing a custom (wrong-key) header
  // was clobbering it and causing 401 → "Failed to load groups".
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/destination-groups`);
      if (r.data?.success) setGroups(r.data.groups || []);
    } catch (e) {
      setError('Failed to load groups');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const countryById = useMemo(() => new Map(allCountries.map((c) => [c.id, c])), [allCountries]);

  const startNew = () => {
    setError('');
    setEditing({ name: '', primary: '', members: [], _new: true });
  };
  const startEdit = (g) => {
    setError('');
    setEditing({
      id: g.id,
      name: g.name || '',
      primary: g.primary_country_id,
      members: (g.members || []).map((m) => ({ ...m })),
    });
  };

  const addMember = (country) => {
    if (!editing || editing.members.find((m) => m.country_id === country.id)) return;
    const next = {
      ...editing,
      members: [...editing.members, { country_id: country.id, label: country.name, descriptor: '' }],
    };
    if (!next.primary) next.primary = country.id;
    if (!next.name) next.name = country.name;
    setEditing(next);
  };

  const removeMember = (id) => {
    const next = { ...editing, members: editing.members.filter((m) => m.country_id !== id) };
    if (next.primary === id) next.primary = next.members[0]?.country_id || '';
    setEditing(next);
  };

  const updateMember = (id, patch) => {
    setEditing({
      ...editing,
      members: editing.members.map((m) => (m.country_id === id ? { ...m, ...patch } : m)),
    });
  };

  const save = async () => {
    if (!editing) return;
    setError('');
    if (!editing.name.trim()) { setError('Name is required'); return; }
    if (editing.members.length < 2) { setError('Add at least two destinations'); return; }
    const payload = {
      name: editing.name.trim(),
      primary_country_id: editing.primary || editing.members[0].country_id,
      members: editing.members.map((m) => ({
        country_id: m.country_id,
        label: m.label || '',
        descriptor: m.descriptor || '',
      })),
    };
    setSaving(true);
    try {
      if (editing.id) {
        await axios.put(`${API}/admin/destination-groups/${editing.id}`, payload);
      } else {
        await axios.post(`${API}/admin/destination-groups`, payload);
      }
      setEditing(null);
      await load();
      refreshDestinationGroups && refreshDestinationGroups();
    } catch (e) {
      setError(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (g) => {
    if (!window.confirm(`Delete group "${g.name}"? Member destinations will reappear as individual cards.`)) return;
    try {
      await axios.delete(`${API}/admin/destination-groups/${g.id}`);
      await load();
      refreshDestinationGroups && refreshDestinationGroups();
    } catch (e) {
      setError('Delete failed');
    }
  };

  const searchResults = useMemo(() => {
    if (!editing || !search.trim()) return [];
    const q = search.toLowerCase();
    return allCountries
      .filter((c) => c.name.toLowerCase().includes(q))
      .filter((c) => !editing.members.find((m) => m.country_id === c.id))
      .slice(0, 8);
  }, [search, allCountries, editing]);

  return (
    <div className="space-y-6" data-testid="admin-destination-grouping">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-black text-gray-900 dark:text-neutral-100">Destination Grouping</h2>
          <p className="text-sm text-gray-500 dark:text-neutral-400 mt-1">
            Collapse variants of the same destination (e.g. Indonesia / Indonesia Lite / Indonesia Premium) into a single card. Users pick a tier from a popup.
          </p>
        </div>
        {!editing && (
          <button
            onClick={startNew}
            className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-xl font-bold hover:bg-gray-800 transition-colors"
            data-testid="admin-group-new-btn"
          >
            <Plus size={16} /> New Group
          </button>
        )}
      </div>

      {error && (
        <div className="px-4 py-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 rounded-xl text-sm">{error}</div>
      )}

      {editing ? (
        <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-gray-200 dark:border-neutral-800 p-6 space-y-5">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold text-gray-900 dark:text-neutral-100">{editing.id ? 'Edit group' : 'New group'}</h3>
            <button onClick={() => setEditing(null)} className="w-9 h-9 rounded-full bg-gray-100 dark:bg-neutral-800 flex items-center justify-center" data-testid="admin-group-close">
              <X size={16} />
            </button>
          </div>

          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-neutral-400 mb-1">Group name (shown on the card)</label>
            <input
              value={editing.name}
              onChange={(e) => setEditing({ ...editing, name: e.target.value })}
              placeholder="e.g. Indonesia"
              className="w-full px-4 py-3 border-2 border-gray-200 dark:border-neutral-800 rounded-xl bg-white dark:bg-neutral-950 focus:border-black outline-none"
              data-testid="admin-group-name-input"
            />
          </div>

          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-neutral-400 mb-1">Search & add destinations</label>
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by country name..."
                className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 dark:border-neutral-800 rounded-xl bg-white dark:bg-neutral-950 focus:border-black outline-none"
                data-testid="admin-group-search"
              />
              {searchResults.length > 0 && (
                <div className="absolute left-0 right-0 top-full mt-1 bg-white dark:bg-neutral-900 border border-gray-200 dark:border-neutral-800 rounded-xl shadow-lg z-10 max-h-64 overflow-y-auto">
                  {searchResults.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => { addMember(c); setSearch(''); }}
                      className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 dark:hover:bg-neutral-800 text-left"
                      data-testid={`admin-group-add-${c.id}`}
                    >
                      {c.isRegion ? (
                        <div className="w-6 h-4 rounded bg-gradient-to-br from-blue-500 to-emerald-500 flex items-center justify-center"><Globe size={10} className="text-white" /></div>
                      ) : (
                        <img src={c.flag} alt="" className="w-6 h-4 rounded object-cover" />
                      )}
                      <span className="text-sm font-medium">{c.name}</span>
                      <span className="text-xs text-gray-400 ml-auto">{c.id}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-neutral-400">Members ({editing.members.length})</label>
            {editing.members.length === 0 ? (
              <div className="text-sm text-gray-500 dark:text-neutral-500 italic">Add two or more destinations to form a group.</div>
            ) : (
              editing.members.map((m) => {
                const c = countryById.get(m.country_id);
                const isPrimary = editing.primary === m.country_id;
                return (
                  <div key={m.country_id} className="flex items-start gap-3 p-3 rounded-xl border border-gray-200 dark:border-neutral-800" data-testid={`admin-group-member-${m.country_id}`}>
                    {c?.isRegion ? (
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-emerald-500 flex items-center justify-center flex-shrink-0"><Globe size={16} className="text-white" /></div>
                    ) : (
                      <img src={c?.flag} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                    )}
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-sm">{c?.name || m.country_id}</span>
                        <span className="text-xs text-gray-400">{m.country_id}</span>
                        <button
                          onClick={() => setEditing({ ...editing, primary: m.country_id })}
                          className={`ml-auto text-xs font-bold px-2 py-1 rounded-full ${isPrimary ? 'bg-emerald-500 text-white' : 'bg-gray-100 dark:bg-neutral-800 text-gray-600 dark:text-neutral-400 hover:bg-gray-200'}`}
                          data-testid={`admin-group-primary-${m.country_id}`}
                        >
                          {isPrimary ? '★ Primary' : 'Make primary'}
                        </button>
                      </div>
                      <input
                        value={m.label}
                        onChange={(e) => updateMember(m.country_id, { label: e.target.value })}
                        placeholder="Variant label (e.g. Standard, Lite, Premium)"
                        className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-neutral-800 rounded-lg bg-white dark:bg-neutral-950 focus:border-black outline-none"
                        data-testid={`admin-group-label-${m.country_id}`}
                      />
                      <textarea
                        value={m.descriptor}
                        onChange={(e) => updateMember(m.country_id, { descriptor: e.target.value })}
                        placeholder="Descriptor shown under the variant name (e.g. Unlimited data, premium network, long validity)"
                        className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-neutral-800 rounded-lg bg-white dark:bg-neutral-950 focus:border-black outline-none resize-none"
                        rows={2}
                        maxLength={160}
                        data-testid={`admin-group-descriptor-${m.country_id}`}
                      />
                    </div>
                    <button onClick={() => removeMember(m.country_id)} className="w-8 h-8 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 hover:bg-red-100 flex items-center justify-center" data-testid={`admin-group-remove-${m.country_id}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                );
              })
            )}
          </div>

          <div className="flex items-center justify-end gap-3 pt-2 border-t border-gray-100 dark:border-neutral-800">
            <button onClick={() => setEditing(null)} className="px-4 py-2 font-bold text-gray-600 dark:text-neutral-400 hover:bg-gray-100 dark:hover:bg-neutral-800 rounded-xl">Cancel</button>
            <button
              onClick={save}
              disabled={saving}
              className="flex items-center gap-2 px-5 py-2 bg-black text-white rounded-xl font-bold hover:bg-gray-800 disabled:opacity-50"
              data-testid="admin-group-save"
            >
              <Save size={16} /> {saving ? 'Saving...' : 'Save group'}
            </button>
          </div>
        </div>
      ) : null}

      <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-gray-200 dark:border-neutral-800 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : groups.length === 0 ? (
          <div className="p-10 text-center">
            <Layers size={28} className="mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500 dark:text-neutral-400">No destination groups yet. Create one to collapse variants into a single card.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-neutral-800">
            {groups.map((g) => (
              <div key={g.id} className="flex items-center gap-4 p-4" data-testid={`admin-group-row-${g.id}`}>
                <Layers size={18} className="text-gray-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-gray-900 dark:text-neutral-100 truncate">{g.name}</div>
                  <div className="text-xs text-gray-500 dark:text-neutral-400 truncate">
                    {(g.members || []).map((m) => m.label || m.country_id).join(' • ')}
                  </div>
                </div>
                <button onClick={() => startEdit(g)} className="px-3 py-1.5 text-sm font-bold text-gray-700 dark:text-neutral-300 bg-gray-100 dark:bg-neutral-800 rounded-lg hover:bg-gray-200" data-testid={`admin-group-edit-${g.id}`}>Edit</button>
                <button onClick={() => remove(g)} className="w-9 h-9 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 hover:bg-red-100 flex items-center justify-center" data-testid={`admin-group-delete-${g.id}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DestinationGroupingPanel;
